import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFetch } from "@/lib/useFetch";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/mockups/ui";
import { Search, FileText, Pencil, FolderKanban, Send, CheckCircle2 } from "lucide-react";

const eur = (n) => (n == null ? "—" : Number(n).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }));
const pct = (n) => `${((n || 0) * 100).toFixed(1)}%`;
const fmtDate = (iso) => { if (!iso) return ""; try { return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit" }); } catch { return ""; } };
const STATUS = {
  active: { label: "Activo", cls: "bg-emerald-100 text-emerald-800" },
  won: { label: "Ganado", cls: "bg-emerald-100 text-emerald-800" },
  lost: { label: "Perdido", cls: "bg-red-100 text-red-700" },
  paused: { label: "Pausado", cls: "bg-amber-100 text-amber-800" },
  closed: { label: "Cerrado", cls: "bg-muted text-muted-foreground" },
};

export default function PresupuestosList() {
  const navigate = useNavigate();
  const { data, loading } = useFetch("/budgets/overview");
  const [q, setQ] = useState("");
  const rows = data?.rows || [];
  const totals = data?.totals || {};

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => `${r.project_name} ${r.company_name} ${r.reference}`.toLowerCase().includes(s));
  }, [rows, q]);

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="presupuestos-list-page">
      <PageHeader title="Presupuestos" subtitle="Todos los presupuestos de todos los proyectos, con sus totales y acceso directo a la propuesta de cliente" />

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        {[
          { label: "Presupuestos", value: totals.count ?? 0, money: false },
          { label: "Total (precio cliente)", value: totals.total, money: true },
          { label: "Coste en horas", value: totals.cost_hours, money: true },
          { label: "Costes externos", value: totals.cost_external, money: true },
          { label: "Margen neto", value: totals.margin_net, money: true },
        ].map((s, i) => (
          <Card key={i} className="border-border shadow-none p-4">
            <div className="font-heading font-bold text-xl tabular-nums">{s.money ? eur(s.value) : (s.value ?? 0)}</div>
            <div className="text-xs text-muted-foreground mt-1">{s.label}</div>
          </Card>
        ))}
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Buscar por proyecto, cliente o referencia…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="presupuestos-search" />
      </div>

      <Card className="border-border shadow-none overflow-hidden">
        {loading ? (
          <div className="py-16 flex justify-center"><div className="h-7 w-7 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-14 text-center text-sm text-muted-foreground" data-testid="presupuestos-empty">No hay presupuestos que coincidan.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table className="min-w-[1040px]">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="pl-5">Proyecto</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Propuesta</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Coste horas</TableHead>
                  <TableHead className="text-right">Externos</TableHead>
                  <TableHead className="text-right">Margen neto</TableHead>
                  <TableHead className="text-right pr-5">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => {
                  const st = STATUS[r.status] || { label: r.status || "—", cls: "bg-muted text-muted-foreground" };
                  return (
                    <TableRow key={r.project_id} data-testid={`presupuesto-row-${r.project_id}`}>
                      <TableCell className="pl-5">
                        <div className="font-medium text-sm">{r.reference ? <span className="text-muted-foreground mr-1">#{r.reference}</span> : null}{r.project_name}</div>
                        <div className="text-xs text-muted-foreground">{r.company_name || "—"} · {r.lines} partidas</div>
                      </TableCell>
                      <TableCell><Badge className={`${st.cls} hover:${st.cls} font-normal`}>{st.label}</Badge></TableCell>
                      <TableCell data-testid={`presupuesto-propstatus-${r.project_id}`}>
                        {r.proposal_status === "accepted" ? (
                          <Badge className="bg-emerald-600 text-white hover:bg-emerald-600 font-normal gap-1"><CheckCircle2 className="h-3 w-3" /> Aceptada v{r.proposal_version}{r.proposal_date ? ` · ${fmtDate(r.proposal_date)}` : ""}</Badge>
                        ) : r.proposal_status === "sent" ? (
                          <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100 font-normal gap-1"><Send className="h-3 w-3" /> Enviada v{r.proposal_version}{r.proposal_date ? ` · ${fmtDate(r.proposal_date)}` : ""}</Badge>
                        ) : r.proposal_status === "draft" ? (
                          <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 font-normal">Borrador v{r.proposal_version}</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">Sin propuesta</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm font-medium">{eur(r.total)}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm text-muted-foreground">{eur(r.cost_hours)}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm text-muted-foreground">{eur(r.cost_external)}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm">
                        <span className={r.margin_net >= 0 ? "text-primary" : "text-destructive"}>{eur(r.margin_net)}</span>
                        <span className="text-xs text-muted-foreground ml-1">({pct(r.margin_pct)})</span>
                      </TableCell>
                      <TableCell className="text-right pr-5">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => navigate(`/proyecto/${r.project_id}?tab=budget`)} data-testid={`presupuesto-edit-${r.project_id}`}><Pencil className="h-3.5 w-3.5" /> Editar</Button>
                          <Button size="sm" className="gap-1.5 h-8" onClick={() => navigate(`/proyecto/${r.project_id}/propuesta`)} data-testid={`presupuesto-proposal-${r.project_id}`}><FileText className="h-3.5 w-3.5" /> Propuesta</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
